import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import './Factory.css';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, ReferenceLine } from 'recharts';
import { fetchFactories, fetchFactoryDashboard, fetchEquipments } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

function Factory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentHour, setCurrentHour] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // API 연동을 위한 상태
  const [factoriesData, setFactoriesData] = useState([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [allEquipments, setAllEquipments] = useState([]); // 모든 사출기 목록
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const refreshIndicatorRef = useRef(null);
  const selectedFactoryIdRef = useRef(null);

  // 사출기 목록 로드
  useEffect(() => {
    const loadEquipments = async () => {
      try {
        const equipments = await fetchEquipments();
        setAllEquipments(equipments || []);
      } catch (err) {
        console.error('사출기 목록 조회 실패:', err);
      }
    };

    loadEquipments();
  }, []);

  // 외부 클릭 감지 - dropdown 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      const factoryDropdown = event.target.closest('.factory-dropdown-wrapper');
      if (!factoryDropdown && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // 1. 공장 목록 조회
  useEffect(() => {
    const loadFactories = async () => {
      try {
        const data = await fetchFactories();

        // 배열인지 확인
        const factories = Array.isArray(data) ? data : [];
        setFactoriesData(factories);

        // 공장이 없으면 로딩 종료
        if (factories.length === 0) {
          setLoading(false);
          return;
        }

        // URL 파라미터에서 factoryId 가져오기
        const factoryIdFromUrl = searchParams.get('factoryId');

        if (factoryIdFromUrl) {
          // URL에 factoryId가 있으면 해당 공장 선택
          const factoryIdNumber = parseInt(factoryIdFromUrl, 10);
          const factoryExists = factories.some(f => f.id === factoryIdNumber);

          if (factoryExists) {
            setSelectedFactoryId(factoryIdNumber);
          } else {
            // URL의 factoryId가 유효하지 않으면 첫 번째 공장 선택
            setSelectedFactoryId(factories[0].id);
          }
        } else {
          // URL에 factoryId가 없으면 첫 번째 공장을 기본 선택
          setSelectedFactoryId(factories[0].id);
        }
      } catch (err) {
        console.error('공장 목록 조회 실패:', err);
        setError('공장 목록을 불러오는데 실패했습니다.');
        setLoading(false);
      }
    };

    loadFactories();
  }, [searchParams]);

  // 대시보드 데이터 로드 함수
  const loadDashboard = useCallback(async (isAutoRefresh = false) => {
    const factoryId = selectedFactoryIdRef.current;
    if (!factoryId) return;

    try {
      if (isAutoRefresh) {
        // CSS 클래스로 애니메이션 처리 (state 대신 ref 사용으로 리렌더링 방지)
        if (refreshIndicatorRef.current) {
          refreshIndicatorRef.current.classList.add('refreshing');
          setTimeout(() => {
            refreshIndicatorRef.current?.classList.remove('refreshing');
          }, 1500);
        }
      } else {
        setLoading(true);
      }
      setError(null);
      const data = await fetchFactoryDashboard(factoryId);
      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('대시보드 데이터 조회 실패:', err);
      if (!isAutoRefresh) {
        setError('데이터를 불러오는데 실패했습니다.');
      }
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  }, []);

  // 2. 선택된 공장의 대시보드 데이터 조회 (초기 로드)
  useEffect(() => {
    if (!selectedFactoryId) return;
    selectedFactoryIdRef.current = selectedFactoryId;
    loadDashboard(false);
  }, [selectedFactoryId, loadDashboard]);

  // useAutoRefresh 훅 사용 (30초 쿨다운 적용)
  useAutoRefresh(() => {
    loadDashboard(true);
  }, !!selectedFactoryId);

  // 3. 시간 업데이트
  useEffect(() => {
    setIsClient(true);
    const updateTime = () => {
      setCurrentHour(new Date().getHours());
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // 현재 선택된 공장 정보
  const currentFactory = factoriesData.find(f => f.id === selectedFactoryId) || {};

  // API 데이터 매핑
  const factoryInfo = dashboardData?.factoryInfo || {};
  const kpiCards = dashboardData?.kpiCards || {
    totalMachines: 0,
    runningMachines: 0,
    idleMachines: 0,
    stoppedMachines: 0,
    overallUtilization: 0,
    dailyProduction: 0,
    energyUsage: 0
  };

  const lineStatuses = dashboardData?.lineStatuses || [];
  const alarmStatus = dashboardData?.alarmStatus || {
    activeCount: 0,
    alarmList: [],
    summary: { errorCount: 0, warningCount: 0, infoCount: 0 }
  };

  // 모든 사출기를 ID로 매핑 (equipmentId -> machine)
  const allMachinesMap = {};

  allEquipments.forEach(equipment => {
    // 다양한 ID 필드명 체크
    const id = equipment.id || equipment.equipmentId || equipment.equipment_id;
    if (id) {
      // id 필드를 표준화하여 저장
      const normalizedEquipment = {
        ...equipment,
        id: id // id 필드가 없으면 추가
      };
      allMachinesMap[id] = normalizedEquipment;
    }
  });

  // 라인별 색상 지정
  const getLineColor = (index) => {
    const colors = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b'];
    return colors[index % colors.length];
  };

  // 유틸리티 함수
  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  const getSeverityColor = (severity) => {
    const normalizedSeverity = severity?.toUpperCase();
    switch (normalizedSeverity) {
      case 'CRITICAL': return 'severity-critical';
      case 'ERROR': return 'severity-error';
      case 'WARNING': return 'severity-warning';
      case 'INFO': return 'severity-info';
      default: return 'severity-default';
    }
  };

  const getSeverityIcon = (severity) => {
    const normalizedSeverity = severity?.toUpperCase();
    switch (normalizedSeverity) {
      case 'CRITICAL': return 'error';
      case 'ERROR': return 'error';
      case 'WARNING': return 'warning';
      case 'INFO': return 'info';
      default: return 'info';
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}분 전`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}시간 전`;
    return date.toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const DonutChart = ({ running, idle, stopped, total, color }) => {
    const operationRate = Math.round((running / total) * 100);

    const pieData = [
      { name: '가동', value: running, color: '#22c55e' },
      { name: '대기', value: idle, color: '#fbbf24' },
      { name: '정지', value: stopped, color: '#ef4444' }
    ];

    const renderLabel = () => {
      return (
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="14" fontWeight="bold">
          {operationRate}%
        </text>
      );
    };

    return (
      <div className="donut-chart-container">
        <div className="donut-chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={45}
                paddingAngle={0}
                dataKey="value"
                stroke="none"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                ))}
              </Pie>
              {renderLabel()}
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="donut-chart-legend">
          <div className="legend-item legend-running">
            <div className="legend-box legend-box-running"></div>
            <span>가동 {running}대</span>
          </div>
          <div className="legend-item legend-idle">
            <div className="legend-box legend-box-idle"></div>
            <span>대기 {idle}대</span>
          </div>
          <div className="legend-item legend-stopped">
            <div className="legend-box legend-box-stopped"></div>
            <span>정지 {stopped}대</span>
          </div>
        </div>
      </div>
    );
  };

  const AlarmTimeline = ({ alarmTimes }) => {
    return (
      <div className="alarm-timeline-container">
        <div className="alarm-timeline-title">알람 발생 시점</div>
        <div className="timeline-wrapper-custom">
          <div className="timeline-bar-background">
            {/* 알람 마커 */}
            {alarmTimes.map((time, index) => (
              <div
                key={`alarm-${index}`}
                className="timeline-marker timeline-marker-alarm"
                style={{ left: `${(time / 24) * 100}%` }}
              />
            ))}

            {/* 현재 시간 마커 */}
            {isClient && (
              <div
                className="timeline-marker timeline-marker-current"
                style={{ left: `${(currentHour / 24) * 100}%` }}
              />
            )}
          </div>

          <div className="timeline-labels">
            <span>0:00</span>
            <span>6:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>

          <div className="timeline-legend">
            <div className="timeline-legend-item">
              <div className="timeline-legend-box timeline-legend-current"></div>
              <span>현재</span>
            </div>
            <div className="timeline-legend-item">
              <div className="timeline-legend-box timeline-legend-alarm"></div>
              <span>알람</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 로딩 상태
  if (loading && !dashboardData) {
    return (
      <MainLayout>
        <div className="factory-container">
          <div className="factory-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div style={{ textAlign: 'center', color: 'white' }}>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>데이터를 불러오는 중...</div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 공장이 없는 경우
  if (!loading && factoriesData.length === 0) {
    return (
      <MainLayout>
        <div className="factory-container">
          <div className="factory-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div style={{ textAlign: 'center', color: 'white' }}>
              <div style={{ fontSize: '18px', marginBottom: '16px' }}>등록된 공장이 없습니다</div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>시스템 관리 메뉴에서 공장을 먼저 등록해주세요</div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <MainLayout>
        <div className="factory-container">
          <div className="factory-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div style={{ textAlign: 'center', color: '#ef4444' }}>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>{error}</div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                다시 시도
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="factory-container">
        <div className="factory-content">
          {/* 헤더 */}
          <div className="enterprise-header">
            <div className="enterprise-header-left">
              <div className="factory-info-main">
                <div className="factory-dropdown-wrapper">
                  <button
                    className="factory-dropdown-trigger"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span className="enterprise-title">{factoryInfo.factoryName || currentFactory.name || '공장 선택'}</span>
                    <span className="material-symbols-outlined dropdown-icon">
                      {isDropdownOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>
                  {isDropdownOpen && (
                    <div className="factory-dropdown-menu">
                      {factoriesData.map((factory) => (
                        <div
                          key={factory.id}
                          className={`factory-dropdown-item ${selectedFactoryId === factory.id ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedFactoryId(factory.id);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="dropdown-item-name">{factory.factoryName}</div>
                          <div className="dropdown-item-location">{factory.address}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="factory-location">{factoryInfo.address || currentFactory.location || ''}</div>
              </div>
            </div>
            <div className="enterprise-header-right" ref={refreshIndicatorRef}>
              <div className="update-label-container">
                <span className="update-label">업데이트</span>
                <span className="material-symbols-outlined refresh-icon">
                  sync
                </span>
              </div>
              <div className="update-time">
                {loading ? '로딩 중...' : (
                  dashboardData?.lastSyncTime
                    ? new Date(dashboardData.lastSyncTime).toLocaleString('ko-KR')
                    : lastUpdated.toLocaleTimeString('ko-KR')
                )}
              </div>
            </div>
          </div>

          {/* KPI 카드들 */}
          <div className="factory-kpi-grid">
            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">총 사출기 대수</div>
                  <div className="kpi-value blue">{kpiCards.totalMachines}</div>
                  <div className="kpi-unit">대</div>
                </div>
                <div className="kpi-icon blue">
                  <span className="material-symbols-outlined">precision_manufacturing</span>
                </div>
              </div>
            </div>

            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">가동중</div>
                  <div className="kpi-value green">{kpiCards.runningMachines}</div>
                  <div className="kpi-unit">대</div>
                </div>
                <div className="kpi-icon green">
                  <span className="material-symbols-outlined">play_circle</span>
                </div>
              </div>
            </div>

            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">대기</div>
                  <div className="kpi-value yellow">{kpiCards.idleMachines}</div>
                  <div className="kpi-unit">대</div>
                </div>
                <div className="kpi-icon yellow">
                  <span className="material-symbols-outlined">pause_circle</span>
                </div>
              </div>
            </div>

            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">정지</div>
                  <div className="kpi-value red">{kpiCards.stoppedMachines}</div>
                  <div className="kpi-unit">대</div>
                </div>
                <div className="kpi-icon red">
                  <span className="material-symbols-outlined">stop_circle</span>
                </div>
              </div>
            </div>

            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">총 가동률</div>
                  <div className="kpi-value green">{kpiCards.overallUtilization}%</div>
                  <div className="kpi-unit">평균</div>
                </div>
                <div className="kpi-icon green">
                  <span className="material-symbols-outlined">speed</span>
                </div>
              </div>
            </div>

            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">일일 생산량</div>
                  <div className="kpi-value yellow">{formatNumber(kpiCards.dailyProduction)}</div>
                  <div className="kpi-unit">EA</div>
                </div>
                <div className="kpi-icon yellow">
                  <span className="material-symbols-outlined">bar_chart</span>
                </div>
              </div>
            </div>

            <div className="factory-kpi-card">
              <div className="kpi-content">
                <div className="kpi-info">
                  <div className="kpi-label">에너지 사용량</div>
                  <div className="kpi-value orange">{formatNumber(kpiCards.energyUsage)}</div>
                  <div className="kpi-unit">kWh</div>
                </div>
                <div className="kpi-icon orange">
                  <span className="material-symbols-outlined">bolt</span>
                </div>
              </div>
            </div>
          </div>

          {/* 하단 영역 */}
          <div className="factory-bottom-grid">
            {/* 좌측: 라인별 가동상태 */}
            <div className="factory-panel">
              <div className="panel-header">
                <h3 className="panel-title">라인별 가동상태</h3>
                <div className="panel-header-buttons">
                  <button className="factory-detail-btn" onClick={() => navigate(`/factory-machine-overview?factoryId=${selectedFactoryId}`)}>
                    사출기별 대시보드
                  </button>
                  <button className="factory-detail-btn" onClick={() => navigate('/realtime-shot-status')}>
                    실시간 샷 현황
                  </button>
                </div>
              </div>

              <div className="line-list">
                {lineStatuses.map((line, index) => (
                  <div
                    key={line.lineId}
                    className="line-item"
                    onClick={() => navigate(`/factory-machine-overview?factoryId=${selectedFactoryId}&lineId=${line.lineId}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="line-content">
                      <DonutChart
                        running={line.running}
                        idle={line.idle}
                        stopped={line.stopped}
                        total={line.machines}
                        color={getLineColor(index)}
                      />

                      <div className="line-details">
                        <h4 className="line-name">{line.lineName}</h4>

                        <div className="line-stats">
                          <div className="stat-item">
                            <div className="stat-label">설비 대수</div>
                            <div className="stat-value">{line.machines}대</div>
                          </div>
                          <div className="stat-item">
                            <div className="stat-label">가동률</div>
                            <div className="stat-value stat-value-green">{line.utilization}%</div>
                          </div>
                          <div className="stat-item">
                            <div className="stat-label">생산량</div>
                            <div className="stat-value stat-value-cyan">{line.production.toLocaleString()} EA</div>
                          </div>
                        </div>

                        <AlarmTimeline alarmTimes={line.alarmTimes || []} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 우측: 환경 데이터 + 알람 리스트 */}
            <div className="factory-right-column">
              {/* 환경 데이터 */}
              <div className="environment-panel">
                <div className="environment-grid">
                  <div className="env-item">
                    <span className="material-symbols-outlined env-icon temp">thermostat</span>
                    <div className="env-info">
                      <span className="env-label">온도</span>
                      <span className="env-value">23.5°C</span>
                    </div>
                  </div>
                  <div className="env-item">
                    <span className="material-symbols-outlined env-icon humidity">humidity_percentage</span>
                    <div className="env-info">
                      <span className="env-label">습도</span>
                      <span className="env-value">55%</span>
                    </div>
                  </div>
                  <div className="env-item">
                    <span className="material-symbols-outlined env-icon air">air</span>
                    <div className="env-info">
                      <span className="env-label">미세먼지</span>
                      <span className="env-value green">12</span>
                    </div>
                  </div>
                  <div className="env-item">
                    <span className="material-symbols-outlined env-icon co2">co2</span>
                    <div className="env-info">
                      <span className="env-label">CO₂</span>
                      <span className="env-value">450ppm</span>
                    </div>
                  </div>
                  <div className="env-item">
                    <span className="material-symbols-outlined env-icon noise">volume_up</span>
                    <div className="env-info">
                      <span className="env-label">소음</span>
                      <span className="env-value">72dB</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 알람 리스트 */}
              <div className="factory-panel alarm-panel">
                <div className="panel-header">
                  <h3 className="panel-title">알람 현황</h3>
                <div className="alarm-header-actions">
                  <div className="active-alarms">
                    <div className="alarm-pulse"></div>
                    <span className="alarm-count">
                      활성 알람: {alarmStatus.activeCount}건
                    </span>
                  </div>
                </div>
              </div>

              <div className="alarm-list">
                {alarmStatus.alarmList.length === 0 ? (
                  <div className="no-alarms-message">
                    <span className="material-symbols-outlined">check_circle</span>
                    <span>진행중인 알람이 없습니다.</span>
                  </div>
                ) : (
                  alarmStatus.alarmList.map((alarm, index) => {
                    const severityClass = getSeverityColor(alarm.severity);
                    const severityIcon = getSeverityIcon(alarm.severity);
                    const equipmentId = alarm.equipmentId || alarm.machineId || alarm.equipment_id || alarm.machine_id;
                    const machineName = alarm.machine || alarm.equipmentName || alarm.machineName;
                    const fullMachineData = allMachinesMap[equipmentId];
                    const machineData = fullMachineData || { id: equipmentId, name: machineName };

                    return (
                      <div
                        key={`${equipmentId}-${alarm.alarmId}-${index}`}
                        className={`alarm-item ${severityClass}`}
                        onClick={() => {
                          navigate('/machine', {
                            state: {
                              machineData: machineData,
                              openTab: 'alarm',
                              alarmId: alarm.alarmId
                            }
                          });
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="alarm-item-header">
                          <span className="material-symbols-outlined alarm-severity-icon">
                            {severityIcon}
                          </span>
                          <span className="alarm-id">#{alarm.alarmId}</span>
                          <span className="alarm-machine">{machineName}</span>
                        </div>
                        <div className="alarm-message">{alarm.message}</div>
                        <div className="alarm-time">발생: {formatRelativeTime(alarm.occurredAt) || alarm.time}</div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="alarm-summary">
                <div className="summary-item">
                  <div className="summary-value summary-value-critical">
                    {alarmStatus.summary.criticalCount}
                  </div>
                  <div className="summary-label">위험</div>
                </div>
                <div className="summary-item">
                  <div className="summary-value summary-value-warning">
                    {alarmStatus.summary.warningCount}
                  </div>
                  <div className="summary-label">경고</div>
                </div>
                <div className="summary-item">
                  <div className="summary-value summary-value-info">
                    {alarmStatus.summary.infoCount}
                  </div>
                  <div className="summary-label">정보</div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default Factory;
